// ========================================
// pages/Register.js - User Registration Page
// ========================================

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';

const Register = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    password: '',
    confirmPassword: '',
	subscribedToNewsletter: false,
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');

const handleChange = (e) => {
  const { name, value, type, checked } = e.target;
  setFormData({
    ...formData,
    [name]: type === 'checkbox' ? checked : value
  });
};

  const validateAge = (dateOfBirth) => {
    if (!dateOfBirth) return false;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 18;
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (!validateAge(formData.dateOfBirth)) {
      setError('You must be at least 18 years old to register');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;
    setLoading(true);

    try {
	  const { confirmPassword, ...registrationData } = formData;
      // registrationData already includes subscribedToNewsletter
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/register`, registrationData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setVerificationEmail(formData.email);
      setVerificationSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/resend-verification`, 
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('Verification email resent! Please check your inbox.');
    } catch (err) {
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="logo-container">
            <img 
              src="/images/symbols/logo-light.png" 
              alt="Agyapadie Vault Logo" 
              className="logo-image" 
            />
          </div>
          <div className="verification-card">
            <div className="verification-icon">📧</div>
            <h2 className="verification-title">Verify Your Email</h2>
            <p className="verification-text">We've sent a verification link to:</p>
            <p className="verification-email">{verificationEmail}</p>
            <p className="verification-text">Please check your inbox (or spam) and click the verification link to activate your account.</p>
            <div className="verification-note">
              <strong>Note:</strong> You won't be able to access your dashboard until your email is verified.
            </div>
            <button onClick={resendVerification} className="btn-resend" disabled={loading}>
              {loading ? 'Sending...' : 'Resend Verification Email'}
            </button>
            <Link to="/login" className="back-link">← Back to Login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="logo-container">
          <img 
            src="/images/symbols/logo-light.png" 
            alt="Agyapadie Vault Logo" 
            className="logo-image" 
          />
        </div>

        <h2 className="auth-title">Create Your Account</h2>
        <p className="auth-subtitle">Start protecting your legacy today</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className="form-input" placeholder="Enter your full name" required />
          </div>

          <div className="form-group">
            <label className="form-label">Email *</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} className="form-input" placeholder="Enter your email" required />
            <small className="form-hint">You'll need to verify this email to activate your account</small>
          </div>

          <div className="form-group">
            <label className="form-label">Phone Number *</label>
            <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} className="form-input" placeholder="0244XXXXXX" required />
          </div>

          <div className="form-group">
            <label className="form-label">Date of Birth *</label>
            <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} className="form-input" required />
            <small className="form-hint">You must be at least 18 years old to register</small>
          </div>

          <div className="form-group">
            <label className="form-label">Password *</label>
            <input type="password" name="password" value={formData.password} onChange={handleChange} className="form-input" placeholder="Minimum 6 characters" required />
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password *</label>
            <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="form-input" placeholder="Re-enter your password" required />
          </div>
<div className="form-group">
  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
    <input
      type="checkbox"
      name="subscribedToNewsletter"
      checked={formData.subscribedToNewsletter}
      onChange={handleChange}
      style={{ width: '18px', height: '18px' }}
    />
    <span style={{ fontSize: '14px', color: '#1A1A1A' }}>
      📧 I would like to receive updates, tips, and offers from Agyapadie Vault
    </span>
  </label>
  <small style={{ fontSize: '11px', color: '#666', marginTop: '5px', display: 'block' }}>
    You can unsubscribe at any time. We respect your privacy.
  </small>
</div>		  

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p className="terms-text">
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="auth-link">Terms of Service</Link> and{' '}
          <Link to="/privacy" className="auth-link">Privacy Policy</Link>
        </p>

        <div className="auth-links">
          <Link to="/login" className="auth-link">Already have an account? Log in</Link>
        </div>

        <Link to="/" className="back-link">← Back to Home</Link>
      </div>
    </div>
  );
};

export default Register;